import React, { useState, useRef } from 'react';
import { useProStatus, PRO_FEATURES } from '../../hooks/useProStatus';
import { setProStatus } from '../../services/purchaseService';

/**
 * Modal shown when users try to access pro features
 */
export function UpgradeModal() {
  const {
    showUpgradeModal,
    setShowUpgradeModal,
    upgradeFeature,
    productInfo,
    purchase,
    restore,
    isLoading,
  } = useProStatus();

  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Dev unlock: tap star 5 times quickly to unlock pro
  const tapCountRef = useRef(0);
  const tapTimerRef = useRef(null);

  const handleDevUnlock = () => {
    tapCountRef.current += 1;

    // Reset tap count after 2 seconds of no taps
    if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
    tapTimerRef.current = setTimeout(() => {
      tapCountRef.current = 0;
    }, 2000);

    // Unlock after 5 taps
    if (tapCountRef.current >= 5) {
      setProStatus(true);
      setSuccessMessage('Dev mode: Pro unlocked!');
      tapCountRef.current = 0;
    }
  };

  if (!showUpgradeModal) return null;

  const handlePurchase = async () => {
    setError(null);
    setSuccessMessage(null);
    const result = await purchase();
    if (!result.success) {
      setError(result.message);
    } else {
      setSuccessMessage('Thank you! Pro features unlocked.');
    }
  };

  const handleRestore = async () => {
    setError(null);
    setSuccessMessage(null);
    const result = await restore();
    if (!result.success) {
      setError(result.message);
    } else {
      setSuccessMessage('Purchases restored successfully!');
    }
  };

  const handleClose = () => {
    setError(null);
    setSuccessMessage(null);
    setShowUpgradeModal(false);
  };

  const featureNames = {
    [PRO_FEATURES.PDF_IMPORT]: 'PDF Import',
    [PRO_FEATURES.PDF_EXPORT]: 'PDF Export',
    [PRO_FEATURES.HVAC_CALCULATIONS]: 'HVAC Load Calculations',
    [PRO_FEATURES.MATERIAL_CALCULATOR]: 'Material Calculator',
    [PRO_FEATURES.ELEVATION_VIEW]: 'Elevation View',
    [PRO_FEATURES.UNLIMITED_PROJECTS]: 'Unlimited Projects',
    [PRO_FEATURES.NO_WATERMARK]: 'Export without Watermark',
    [PRO_FEATURES.ALL_HATCH_PATTERNS]: 'All Hatch Patterns',
    [PRO_FEATURES.ESTIMATES]: 'Estimates',
    [PRO_FEATURES.INVOICES]: 'Invoices',
    [PRO_FEATURES.COMPANY_PROFILE]: 'Company Profile',
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 99999,
      padding: '16px',
    }}>
      <div style={{
        backgroundColor: '#1f2937',
        borderRadius: '12px',
        maxWidth: '400px',
        width: '100%',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(to right, #0891b2, #2563eb)',
          padding: '24px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '40px', marginBottom: '8px', cursor: 'pointer', userSelect: 'none' }} onClick={handleDevUnlock}>⭐</div>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffffff', margin: 0 }}>Upgrade to Pro</h2>
          {upgradeFeature && (
            <p style={{ color: '#e0f2fe', marginTop: '8px', fontSize: '14px' }}>
              {featureNames[upgradeFeature] || upgradeFeature} is a Pro feature
            </p>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {/* Pro features list */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ color: '#ffffff', fontWeight: '600', marginBottom: '12px', fontSize: '16px' }}>Pro includes:</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {[
                { icon: '📄', text: 'PDF Import & Export' },
                { icon: '❄️', text: 'HVAC Load Calculations' },
                { icon: '📦', text: 'Material Calculator & Estimates' },
                { icon: '🏠', text: 'Elevation Views' },
                { icon: '📁', text: 'Unlimited Projects' },
                { icon: '✨', text: 'All Hatch Patterns' },
                { icon: '🎨', text: 'Export without Watermark' },
              ].map((feature, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#e5e7eb', marginBottom: '8px', fontSize: '14px' }}>
                  <span style={{ fontSize: '18px' }}>{feature.icon}</span>
                  <span>{feature.text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Price */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ffffff' }}>
              {productInfo?.price || '$9.99'}
            </div>
            <div style={{ color: '#9ca3af', fontSize: '14px' }}>One-time purchase</div>
          </div>

          {/* Error/Success messages */}
          {error && (
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.5)', borderRadius: '8px', color: '#fca5a5', fontSize: '14px', textAlign: 'center' }}>
              {error}
            </div>
          )}
          {successMessage && (
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: 'rgba(34, 197, 94, 0.2)', border: '1px solid rgba(34, 197, 94, 0.5)', borderRadius: '8px', color: '#86efac', fontSize: '14px', textAlign: 'center' }}>
              {successMessage}
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button
              onClick={handlePurchase}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '14px 16px',
                background: 'linear-gradient(to right, #06b6d4, #3b82f6)',
                color: '#ffffff',
                fontWeight: '600',
                fontSize: '16px',
                borderRadius: '8px',
                border: 'none',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.5 : 1,
              }}
            >
              {isLoading ? 'Processing...' : `Unlock Pro - ${productInfo?.price || '$9.99'}`}
            </button>

            <button
              onClick={handleRestore}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '10px 16px',
                background: 'transparent',
                color: '#22d3ee',
                fontSize: '14px',
                border: 'none',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.5 : 1,
              }}
            >
              Restore Previous Purchase
            </button>

            <button
              onClick={handleClose}
              style={{
                width: '100%',
                padding: '10px 16px',
                background: 'transparent',
                color: '#9ca3af',
                fontSize: '14px',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Simple upgrade prompt for inline use
 */
export function UpgradePrompt({ feature, children }) {
  const { isPro, requirePro } = useProStatus();

  if (isPro) {
    return children;
  }

  return (
    <div
      onClick={() => requirePro(feature)}
      className="cursor-pointer"
    >
      <div className="relative">
        <div className="opacity-50 pointer-events-none">
          {children}
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded">
          <span className="bg-cyan-500 text-white text-xs px-2 py-1 rounded font-medium">
            PRO
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Pro badge for labeling pro features
 */
export function ProBadge({ className = '' }) {
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded ${className}`}>
      PRO
    </span>
  );
}

export default UpgradeModal;
